import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { ContentItemViewNodesType } from 'components/WorkspaceEditor/types'
import { TrackChangeType } from 'models/changes'

/**
 * Checks if article text already exists in workspace (case-insensitive, excludes hidden articles)
 * @param {string} text - Text to validate
 * @param {Partial<ContentItemViewNodesType>[]} contentNodes - Workspace content nodes
 * @returns {boolean} true if duplicate found
 */
export const isDuplicateArticle = (
    text: string,
    contentNodes: Partial<ContentItemViewNodesType>[]
): boolean => {
    // Guard against invalid input
    if (!contentNodes || !Array.isArray(contentNodes)) {
        return false
    }

    // Filter for visible articles only
    const articles = contentNodes.filter(
        (node) => node.textType === 'Article' && !node.isHidden
    )

    // Normalize input text for case-insensitive comparison
    const normalizedText = text.trim().toLowerCase()

    return articles.some((node) => {
        // Single element comparison - most common case
        if (node.elements?.[0]?.text?.trim().toLowerCase() === normalizedText) {
            return true
        }

        // Multi-element comparison - concatenate non-deleted elements
        if (node.elements && node.elements.length > 1) {
            const fullText = node.elements
                .filter(
                    (element) => element.changeType !== TrackChangeType.DELETED
                )
                .map((element) => element.text || '')
                .join('')
                .toLowerCase()

            return fullText === normalizedText
        }

        return false
    })
}

const TRAILING_PUNCTUATION_RE = /[.,;:!?]+$/

/** Normalizes a key string for comparison: lowercase, trim, strip trailing punctuation */
const normalizeKey = (key: string): string =>
    key.toLowerCase().trim().replace(TRAILING_PUNCTUATION_RE, '')

/**
 * Extracts the paragraph key from a node's elements (text before the first colon).
 * Handles multi-element keys where text is split across elements.
 * Skips DELETED elements. Returns normalized key.
 */
export const extractParagraphKey = (
    elements: Partial<TextContentElement>[]
): string => {
    let key = ''
    let foundColon = false

    for (const element of elements) {
        if (!element?.text || element.changeType === TrackChangeType.DELETED)
            continue
        const text = element.text

        if (text.startsWith(':')) {
            foundColon = true
            break
        }

        const colonIdx = text.indexOf(':')
        if (colonIdx !== -1) {
            key += text.substring(0, colonIdx)
            foundColon = true
            break
        }
        key += text
    }
    return foundColon ? normalizeKey(key) : ''
}

/**
 * Extracts a paragraph key from raw text (text before the first colon).
 * Used in ADD flow where content is a single string, not yet split into elements.
 */
export const extractParagraphKeyFromText = (text: string): string => {
    const colonIdx = text.indexOf(':')

    if (colonIdx === -1) return ''
    return normalizeKey(text.substring(0, colonIdx))
}

/**
 * Checks if a paragraph key already exists among sibling nodes (case-insensitive).
 * Skips hidden siblings.
 */
export const isDuplicateParagraphKey = (
    keyText: string,
    siblings: Partial<ContentItemViewNodesType>[]
): boolean => {
    const normalized = normalizeKey(keyText)

    if (!normalized) return false

    return siblings.some((sibling) => {
        if (sibling.isHidden) return false

        const siblingKey = extractParagraphKey(sibling.elements ?? [])
        return siblingKey === normalized
    })
}
